import {RouteComponentProps} from "react-router";
import {Fragment, useEffect, useState, useMemo, useCallback, useRef} from "react";
import {CircularProgress, TableCell, TableRow, Grid, Paper, Table, TableBody, Button, Modal, Icon, Tooltip, Checkbox, FormControlLabel} from "@material-ui/core";
import {DateTimePicker, KeyboardDateTimePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import ArtApi from "../../api/ArtApi";
import ISensorIndirection from "../../models/SensorIndirection"
import {TaggingChart} from "../../components/EventChart";
import {RheemEventCandidates} from "./RheemEventCandidates";
import {TransparentInput} from "../../components/TransparentInput";
import {MaterialUiPickersDate} from "@material-ui/pickers/typings/date";
import DateFnsUtils from '@date-io/date-fns';
import { formatUTC } from "../../lib/helpers";
import ErrorSnackbar from "../../components/ErrorSnackbar";

/**
 * Converts a local Date object to UTC by treating the local date/time components as if they were UTC.
 * This creates a new Date with the same year, month, day, hours, minutes, and seconds,
 * but interpreted as UTC rather than local time.
 *
 * @param date - The local Date object to convert
 * @returns A new Date object with the same components interpreted as UTC
 *
 * @example
 * // If local time is 2024-01-15 10:30:00 EST (UTC-5)
 * const localDate = new Date(2024, 0, 15, 10, 30, 0);
 * const utcDate = treatDateAsUTC(localDate);
 * // utcDate will represent 2024-01-15 10:30:00 UTC (not 15:30:00 UTC)
 */
const treatDateAsUTC = (date: Date) => {
    return new Date(Date.UTC(date.getFullYear(),date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
}

const CreateEventModal = (props: any) => {
    const [eventType, setEventType] = useState<string>("HVACEvent");
    const [eventSubType, setEventSubType] = useState<string>("");
    const [eventDescription, setEventDescription] = useState<string>("");
    const [startTime, setStartTime] = useState<Date>(new Date());
    const [endTime, setEndTime] = useState<Date | null>(null);
    const [noPenaltyForMissing, setNoPenaltyForMissing] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!props.open) {
        return null;
    }

    const containerStyle = {
        display: 'flex',
        padding: '20px',
        margin: 'auto',
        flexDirection: 'column' as 'column',
        background: '#002244',
        width: '30%',
    }

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <div style={containerStyle} className="create-event-modal-container">
                <h2>Create Event</h2>
                <TransparentInput
                    placeholder={"Event Type"}
                    required
                    onChange={(e: any) => setEventType(e.target.value)}
                    value={eventType}
                />
                <TransparentInput
                    placeholder={"Event SubType"}
                    required
                    onChange={(e: any) => setEventSubType(e.target.value)}
                    value={eventSubType}
                />
                <TransparentInput
                    placeholder={"Event Description"}
                    required
                    onChange={(e: any) => setEventDescription(e.target.value)}
                    value={eventDescription}
                />
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <DateTimePicker label={"Start Time"} value={startTime} onChange={(e: MaterialUiPickersDate) => e && setStartTime(e)}
                        format="yyyy-MM-dd HH:mm 'UTC'"/>
                    <DateTimePicker label={"End Time"} value={endTime} onChange={(e: MaterialUiPickersDate) => e && setEndTime(e)}
                        format="yyyy-MM-dd HH:mm 'UTC'"/>
                </MuiPickersUtilsProvider>
                <FormControlLabel
                    control={<Checkbox checked={noPenaltyForMissing} onChange={(e) => setNoPenaltyForMissing(e.target.checked)} />}
                    label="No Penalty For Missing"
                />
                <Button variant={"contained"} onClick={() => {
                    setLoading(true);
                    const utcStartDate = treatDateAsUTC(startTime);
                    const utcEndDate = endTime ? treatDateAsUTC(endTime) : null;
                    props.createEvent(eventType, eventSubType, eventDescription, utcStartDate, utcEndDate, noPenaltyForMissing);
                    setLoading(false);
                }} disabled={loading}>Create Event</Button>
            </div>
        </Modal>
    )
}


const NotesTable = (props: any) => {
    const [newNoteTimestamp, setTimestamp] = useState<Date | null>(null);
    const [newNote, setNewNote] = useState<string>("");
    const [notes, setNotes] = useState<any[]>([]);
    const [noteEdits, setNoteEdits] = useState<{[key: string]: string}>({});
    const mountedRef = useRef(true);

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        ArtApi.getNotes(props.coreId).then(response => {
            if (!mountedRef.current) return;
            setNotes(response.response);
        });
    }, [props.coreId]);

    return (
        <Table>
            <TableBody>
                <TableRow>
                    <TableCell>
                        <TransparentInput
                            placeholder={"Add a note"}
                            value={newNote}
                            onChange={(e: any) => setNewNote(e.target.value)}
                            onBlur={() => {}}
                            width={"300px"}
                        />
                    </TableCell>
                    <TableCell>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DateTimePicker label="Add Timestamp" value={newNoteTimestamp} onChange={(newValue) => setTimestamp(newValue)} format="yyyy-MM-dd HH:mm 'UTC'" />
                        </MuiPickersUtilsProvider>
                    </TableCell>
                    <TableCell>
                        <Button variant={"contained"} onClick={() => {
                            if(newNote.trim() === "") {
                                return;
                            }
                            ArtApi.createNote(props.coreId, newNote, newNoteTimestamp ? newNoteTimestamp.getTime() : null).then(() => {
                                if (!mountedRef.current) return;
                                setNewNote("");
                                ArtApi.getNotes(props.coreId).then(response => {
                                    if (!mountedRef.current) return;
                                    setNotes(response.response);
                                });
                            });
                        }}>Add</Button>
                    </TableCell>
                </TableRow>
                {notes && notes.map(note => {
                    let timestamp = "";
                    if (note.timestamp) {
                        // note.timestamp is already in milliseconds from the backend
                        timestamp = formatUTC(Math.floor(note.timestamp / 1000));
                    }
                    // note.created_at is already in milliseconds from the backend
                    const createdAt = formatUTC(Math.floor(note.created_at / 1000));

                    return (
                        <TableRow key={note.id}>
                            <TableCell>
                                <TransparentInput
                                    tooltip={note.note}
                                    value={noteEdits[note.id] || note.note}
                                    onChange={(e: any) => {
                                        setNoteEdits({...noteEdits, [note.id]: e.target.value});
                                    }}
                                    onBlur={() => {
                                        if (!noteEdits[note.id] || noteEdits[note.id] === note.note) {
                                            return;
                                        }

                                        ArtApi.updateNote(note.id, noteEdits[note.id], note.timestamp).then(() => {
                                            if (!mountedRef.current) return;
                                            ArtApi.getNotes(props.coreId).then(response => {
                                                if (!mountedRef.current) return;
                                                setNotes(response.response);
                                            });
                                        });
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                {`Created at ${createdAt}`}
                            </TableCell>
                            <TableCell>
                                {timestamp}
                            </TableCell>
                            <TableCell>
                                <Button variant={"contained"} onClick={() => {
                                    ArtApi.deleteNote(note.id).then(() => {
                                        if (!mountedRef.current) return;
                                        ArtApi.getNotes(props.coreId).then(response => {
                                            if (!mountedRef.current) return;
                                            setNotes(response.response);
                                        });
                                    });
                                }}>Delete</Button>
                            </TableCell>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}

type RheemSensorDetailsProps = RouteComponentProps<{
    coreId: string;
}>;

export const RheemSensorDetails = (props: RheemSensorDetailsProps) => {
    const [error, setError] = useState<string | null>(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sensorIndirection, setSensorIndirection] = useState<ISensorIndirection | null>(null);
    const [metadata, setMetadata] = useState<any | null>(null);
    const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
    const [staleEventCandidates, setStaleEventCandidates] = useState(new Date());

    const getInitialDates = () => {
        const now = new Date();
        const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
        
        // Set to start of hour
        now.setMinutes(0, 0, 0);
        threeDaysAgo.setMinutes(0, 0, 0);
        
        return { start: threeDaysAgo, end: now };
    };

    const initialDates = useMemo(() => getInitialDates(), []);

    const MAX_CHART_RANGE_DAYS = 14;

    // UI state
    const [start, setStart] = useState(initialDates.start);
    const [end, setEnd] = useState(initialDates.end);

    const chartRangeTooLong = useMemo(() => {
        const spanInDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
        return spanInDays > MAX_CHART_RANGE_DAYS;
    }, [start, end]);
    
    // pushed to getChart from update button
    const [fetchStart, setFetchStart] = useState(initialDates.start);
    const [fetchEnd, setFetchEnd] = useState(initialDates.end);

    useEffect(() => {
        setIsLoading(true);
        ArtApi.getSensorIndirection(props.match.params.coreId)
            .then(response => {
                setIsLoading(false);
                setSensorIndirection(response.response);
                if (response.error) {
                    setError(response.error);
                    setSnackbarOpen(true);
                }
            }).catch(error => {
            setIsLoading(false);
            setError(error.message);
            setSnackbarOpen(true);
        })
    }, [props.match.params.coreId]);

    const macAddress = useMemo(() => {
        if (sensorIndirection) {
            return sensorIndirection.hardwareIDs?.macAddress ?? "unknown";
        }
    }, [sensorIndirection]);

    const getChart = useCallback(() => {
        return ArtApi.getRheemChart(
            props.match.params.coreId,
            fetchStart.toISOString(),
            fetchEnd.toISOString(),
            'r',
        );
    }, [fetchStart, fetchEnd, props.match.params.coreId]);

    useEffect(() => {
        if (!macAddress) {
            return;
        }

        ArtApi.getRheemMetadata(macAddress).then(response => {
            setMetadata(response.response);
        });
    }, [macAddress]);


    const buildMetadataTable = useCallback(() => {
        if (!metadata) {
            return null;
        }

        // generator function that returns key-value pairs, recursing on nested objects
        function* flatten(obj: any, prefix: string = ''): Generator<[string, any]> {
            for (const key of Object.keys(obj)) {
                const value = obj[key];
                if (typeof value === 'object') {
                    yield* flatten(value, `${prefix}${key}.`);
                } else {
                    yield [`${prefix}${key}`, value];
                }
            }
        }

        const flattened = [...flatten(metadata)];
        return (
            <Table>
                <TableBody>
                    {flattened.map(([key, val]) => {
                        return (
                            <TableRow key={key}>
                                <TableCell>
                                    {key}
                                </TableCell>
                                <TableCell>
                                    {typeof val === "boolean" ? <Icon>{val ? "check_circle" : "close"}</Icon> : val}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        )
    }, [metadata]);

    const commitApprovedEventsClicked = useCallback(() => {
        // return ArtApi.commitApprovedEvents();
        return ArtApi.commitApprovedEvents().then(() => {
            setStaleEventCandidates(new Date());
        });
    }, [setStaleEventCandidates])

    return (
        <Fragment>
            <h1>Rheem Sensor Details</h1>
            {isLoading && <CircularProgress color="secondary" />}
            <ErrorSnackbar message={error} open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />

            {!isLoading &&
                <Grid container spacing={3}>
                    {createEventModalOpen &&
                        <CreateEventModal
                            open={createEventModalOpen}
                            onClose={() => setCreateEventModalOpen(false)}
                            createEvent={(eventType: string, eventSubType: string, eventDescription: string, startTime: Date, endTime: Date, noPenaltyForMissing: boolean) => {
                                ArtApi.createEvent(props.match.params.coreId, eventType, eventSubType, eventDescription, startTime, endTime, noPenaltyForMissing).then(() => {
                                    setStaleEventCandidates(new Date());
                                    setCreateEventModalOpen(false);
                                });
                            }}
                        />
                    }

                    <Grid item>
                        <h2>Details</h2>
                        <Paper className="paper">
                            <Table>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            Core ID
                                        </TableCell>
                                        <TableCell>
                                            {sensorIndirection?._id}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            MAC Address
                                        </TableCell>
                                        <TableCell>
                                            {macAddress}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Paper>
                    </Grid>

                    {metadata &&
                        <Grid item>
                            <h2>Metadata</h2>
                            <Paper className="paper">
                                {buildMetadataTable()}
                            </Paper>
                        </Grid>
                    }

                    <Grid item>
                        <h2>Notes</h2>
                        <Paper className="paper">
                            <NotesTable coreId={props.match.params.coreId} />
                        </Paper>
                    </Grid>

                    <Grid item>
                        <h2>
                            Event Candidates
                            <Button style={{display: "inline-block", marginLeft: "50px"}} variant={"outlined"} onClick={() => setCreateEventModalOpen(true)}>Create Event</Button>
                            {/* Disabled: commitApproved depends on AlertLabsApi, which is being removed from the Rheem product class.
                                A disabled MUI button doesn't fire hover events, so the Tooltip wraps a <span>. */}
                            <Tooltip title="Disabled because the connection to the production system has been severed.">
                                <span style={{display: "inline-block", marginLeft: "50px"}}>
                                    <Button disabled variant={"outlined"} onClick={() => commitApprovedEventsClicked()}>Commit Approved Events</Button>
                                </span>
                            </Tooltip>
                        </h2>

                        <Paper className="paper">
                            <RheemEventCandidates coreId={props.match.params.coreId} staleAt={staleEventCandidates} />
                        </Paper>
                    </Grid>

                    <Grid item>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <KeyboardDateTimePicker 
                                label="Chart Start Time (UTC)"
                                format="yyyy-MM-dd HH:mm" 
                                id="start-time-input" 
                                value={start} 
                                onChange={(e: MaterialUiPickersDate) => {
                                    if (e && !isNaN(e.getTime())) {
                                        setStart(e);
                                        // If new start is after end, adjust end to match start
                                        if (e > end) {
                                            setEnd(e);
                                        }
                                    }
                                }}
                                placeholder="yyyy-MM-dd HH:mm"
                                inputVariant="outlined"
                                ampm={false}
                                autoOk={true}
                            />
                            <br /><br />

                            <KeyboardDateTimePicker
                                label="Chart End Time (UTC)"
                                format="yyyy-MM-dd HH:mm"
                                id="end-time-input"
                                value={end}
                                onChange={(e: MaterialUiPickersDate) => {
                                    if (e && !isNaN(e.getTime())) {
                                        setEnd(e);
                                        // If new end is before start, adjust start to match end
                                        if (e < start) {
                                            setStart(e);
                                        }
                                    }
                                }}
                                placeholder="yyyy-MM-dd HH:mm"
                                inputVariant="outlined"
                                ampm={false}
                                autoOk={true}
                                error={chartRangeTooLong}
                                helperText={chartRangeTooLong ? `Please select a period shorter than ${MAX_CHART_RANGE_DAYS} days.` : undefined}
                            />
                        </MuiPickersUtilsProvider>
                        <br></br>
                        <br></br>
                        <Button variant="contained" disabled={chartRangeTooLong} onClick={() => {
                            setFetchStart(start);
                            setFetchEnd(end);
                        }}>
                            Update Chart
                        </Button>
                    </Grid>
                    <Grid item>
                        <h3>Top Level IDs</h3>
                        <Table>
                            <TableBody>
                            <TableRow><TableCell>-1</TableCell><TableCell>unknown</TableCell></TableRow>
                            <TableRow><TableCell>0</TableCell><TableCell>off</TableCell></TableRow>
                            <TableRow><TableCell>1</TableCell><TableCell>cool</TableCell></TableRow>
                            <TableRow><TableCell>2</TableCell><TableCell>heat_pumping</TableCell></TableRow>
                            <TableRow><TableCell>3</TableCell><TableCell>defrost</TableCell></TableRow>
                            <TableRow><TableCell>4</TableCell><TableCell>fan</TableCell></TableRow>
                            <TableRow><TableCell>5</TableCell><TableCell>aux_heat</TableCell></TableRow>
                            <TableRow><TableCell>6</TableCell><TableCell>elec_heat</TableCell></TableRow>
                            <TableRow><TableCell>7</TableCell><TableCell>heat_mode</TableCell></TableRow>
                            <TableRow><TableCell>8</TableCell><TableCell>heat_defrost_unknown</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid item>
                        <h3>Sub-Mode IDs</h3>
                        <Table>
                            <TableBody>
                            <TableRow><TableCell>0</TableCell><TableCell>unknown</TableCell><TableCell>11</TableCell><TableCell>low_cooling</TableCell></TableRow>
                            <TableRow><TableCell>12</TableCell><TableCell>int_cooling</TableCell><TableCell>13</TableCell><TableCell>high_cooling</TableCell></TableRow>
                            <TableRow><TableCell>14</TableCell><TableCell>over_cooling</TableCell><TableCell>21</TableCell><TableCell>low_heating</TableCell></TableRow>
                            <TableRow><TableCell>22</TableCell><TableCell>int_heating</TableCell><TableCell>23</TableCell><TableCell>high_heating</TableCell></TableRow>
                            <TableRow><TableCell>24</TableCell><TableCell>over_heating</TableCell><TableCell>31</TableCell><TableCell>elec_low_defrost</TableCell></TableRow>
                            <TableRow><TableCell>32</TableCell><TableCell>elec_med_defrost</TableCell><TableCell>33</TableCell><TableCell>elec_high_defrost</TableCell></TableRow>
                            <TableRow><TableCell>34</TableCell><TableCell>low_furn_defrost</TableCell><TableCell>35</TableCell><TableCell>high_furn_defrost</TableCell></TableRow>
                            <TableRow><TableCell>36</TableCell><TableCell>defrost</TableCell><TableCell>51</TableCell><TableCell>high_heat_low_elec</TableCell></TableRow>
                            <TableRow><TableCell>52</TableCell><TableCell>high_heat_med_elec</TableCell><TableCell>53</TableCell><TableCell>high_heat_high_elec</TableCell></TableRow>
                            <TableRow><TableCell>61</TableCell><TableCell>aux_low_heat</TableCell><TableCell>62</TableCell><TableCell>aux_med_heat</TableCell></TableRow>
                            <TableRow><TableCell>63</TableCell><TableCell>aux_high_heat</TableCell><TableCell>64</TableCell><TableCell>aux_furn_heat</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                    <Grid item>
                        <h3>Stat Modes</h3>
                        <Table>
                            <TableBody>
                            <TableRow><TableCell>Heating</TableCell><TableCell>0</TableCell></TableRow>
                            <TableRow><TableCell>Cooling</TableCell><TableCell>1</TableCell></TableRow>
                            <TableRow><TableCell>Auto</TableCell><TableCell>2</TableCell></TableRow>
                            <TableRow><TableCell>Fan Only</TableCell><TableCell>3</TableCell></TableRow>
                            <TableRow><TableCell>Off</TableCell><TableCell>4</TableCell></TableRow>
                            <TableRow><TableCell>Emergency Heat</TableCell><TableCell>5</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                    {/* <TaggingChart eventId={props.match.params.coreId} getChartFn={getChart} onChartClick={createNote} /> */}
                    <TaggingChart eventId={props.match.params.coreId} getChartFn={getChart} />
                </Grid>
            }
        </Fragment>
    )
}
